/**
 * Legacy UI contracts — перенесено из apps/frontend/src/shared-stub/index.ts.
 *
 * Эти схемы используются frontend-ом для страниц/flows, где backend ещё не
 * материализовал canonical контракт. Их надо постепенно мигрировать в
 * `api-contracts.ts` / `entities/` / `events.ts`, но на момент миграции
 * shared-stub → @staiter/shared они консервируются здесь без изменений
 * семантики, чтобы не ломать существующие UI-flows.
 *
 * Правила:
 *  - сюда НЕ добавляются новые типы. Новые контракты живут в канонических
 *    модулях (api-contracts / entities / events).
 *  - при materialization бэкендом — переносим в соответствующий канонический
 *    модуль и удаляем дубликат отсюда.
 */
import { z } from 'zod';
import {
  Currency,
  JobStatus,
  MemoryLifecycle,
  MemoryScope,
  MemorySensitivity,
  Role,
  RunBusinessStatus,
  TaskType,
  ValuationMethod,
} from './enums.js';

// ─── Extended enums (stub-only, не дублируют shared/enums) ────────────

// Role — canonical, импортируется из enums.js (расширен: warehouse_operator/production_planner).

export const ThemePreference = z.enum(['light', 'dark', 'system']);
export type ThemePreference = z.infer<typeof ThemePreference>;

export const ConflictSeverity = z.enum(['low', 'medium', 'high', 'critical']);
export type ConflictSeverity = z.infer<typeof ConflictSeverity>;

export const SourceLevel = z.enum(['L1', 'L2', 'L3', 'L4', 'L5']);
export type SourceLevel = z.infer<typeof SourceLevel>;

export const ApprovalStatus = z.enum([
  'pending',
  'approved',
  'rejected',
  'expired',
  // S-FIX-4: prod has 'waiting' (alias of 'pending' from older approvals queue).
  'waiting',
  // B3 (tier2-approval-requests) — исходы очереди НАМЕРЕНИЙ: заявка исполняется
  // прямо сейчас или отозвана инициатором. Без них экран очереди упал бы на разборе.
  'executing',
  'withdrawn',
  // B74 (Ф6) — сбой после ответа 1С у заявки уровня 3.
  'executed_unconfirmed',
]);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

export const ApprovalTier = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]);
export type ApprovalTier = z.infer<typeof ApprovalTier>;

export const PeriodLockState = z.enum(['open', 'locked', 'reopened']);
export type PeriodLockState = z.infer<typeof PeriodLockState>;

export const PeriodCloseBlockerKind = z.enum([
  'open_conflicts',
  'pending_approvals',
  'failed_runs',
  'unsubmitted_stock_entries',
  'missing_reconciliation',
  // S-FIX-2-BE.7.1 / FE.7.1 — расширение для S14 Phase 2 + landed cost + master drift.
  'unsubmitted_landed_cost',
  'unresolved_master_drift',
  'draft_invoices',
  'draft_payments',
  'draft_settlements',
]);
export type PeriodCloseBlockerKind = z.infer<typeof PeriodCloseBlockerKind>;

/**
 * S-FIX-9-BE §4 — Stock reconciliation kind.
 * SYNC §1.2: расширен до 4 значений для покрытия реальной модели:
 *  - opening_stock — onboarding (B-вариант), единственный SLE до date
 *  - standard — ad-hoc spot count
 *  - periodic — квартальная/годовая (на проде такие есть)
 *  - cycle_count — ABC-cycle (ERPNext pattern)
 *
 * BE-2 (2026-05-20): real BE additionally emits `physical_count` и
 * `variance_adjustment` — добавлены как aliases чтобы FE Zod parse не падал.
 * `physical_count` — синоним standard (ручной count); `variance_adjustment` —
 * post-count adjustment запись.
 */
export const StockReconciliationKind = z.enum([
  'opening_stock',
  'standard',
  'periodic',
  'cycle_count',
  'physical_count',
  'variance_adjustment',
]);
export type StockReconciliationKind = z.infer<typeof StockReconciliationKind>;

export const VarianceSeverity = z.enum([
  'match',
  'within_tolerance',
  'variance',
  'critical_variance',
]);
export type VarianceSeverity = z.infer<typeof VarianceSeverity>;

export const LandedCostBasis = z.enum(['by_qty', 'by_amount', 'by_weight']);
export type LandedCostBasis = z.infer<typeof LandedCostBasis>;

export const OnecPushKind = z.enum(['master_data', 'documents', 'aggregates']);
export type OnecPushKind = z.infer<typeof OnecPushKind>;

export const OnecPushStatus = z.enum(['pending', 'success', 'failed', 'rolled_back']);
export type OnecPushStatus = z.infer<typeof OnecPushStatus>;

export const OverrideDecision = z.enum(['pending', 'accept', 'defer', 'reject']);
export type OverrideDecision = z.infer<typeof OverrideDecision>;

export const OverrideDecisionValueLegacy = OverrideDecision;

export const OverrideSemanticField = z.enum(['qty', 'valuation_rate', 'note', 'other']);
export type OverrideSemanticField = z.infer<typeof OverrideSemanticField>;

export const CostScopeKind = z.enum(['period', 'sku', 'work_order']);
export type CostScopeKind = z.infer<typeof CostScopeKind>;

export const BatchQcState = z.enum([
  'qc_pending',
  'qc_passed',
  'qc_failed',
  'quarantine',
  'released',
  'disposed',
  'recalled',
]);
export type BatchQcState = z.infer<typeof BatchQcState>;

export const BatchQcTransition = z.enum([
  'qc-pass',
  'qc-fail',
  'send-to-quarantine',
  'release',
  'dispose',
  'recall',
]);
export type BatchQcTransition = z.infer<typeof BatchQcTransition>;

export const MovePreviewReason = z.enum(['cycle', 'depth_exceeded', 'has_stock', 'self_parent']);
export type MovePreviewReason = z.infer<typeof MovePreviewReason>;

export const BulkImportResource = z.enum([
  'items',
  'warehouses',
  'batches',
  'boms',
  'counterparties',
]);
export type BulkImportResource = z.infer<typeof BulkImportResource>;

export const BulkImportIssueSeverity = z.enum(['error', 'warning']);
export type BulkImportIssueSeverity = z.infer<typeof BulkImportIssueSeverity>;

export const MasterDriftFieldPick = z.enum(['ours', 'theirs', 'merge']);
export type MasterDriftFieldPick = z.infer<typeof MasterDriftFieldPick>;

/**
 * @deprecated W1.4 — use canonical `WorkOrderStatus` from `entities/work-order.ts`.
 * Legacy WoState оставлен как alias для backward-compat. Добавлено `draft` (W1.4 canonical parity).
 */
export const WoState = z.enum([
  'draft',
  'planned',
  'released',
  'in_progress',
  'paused_external',
  'completed',
  'closed',
  'cancelled',
]);
export type WoState = z.infer<typeof WoState>;

// ─── Generic list helper ──────────────────────────────────────────────

export const CursorList = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), next_cursor: z.string().nullable() });

// ─── Auth / Admin ─────────────────────────────────────────────────────

export const LoginRequest = z.object({
  email: z.string().email({ message: 'Введите корректный email' }),
  password: z.string().min(1, { message: 'Пароль обязателен' }),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const LoginResponse = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  must_reset: z.boolean().optional(),
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: Role,
    full_name: z.string().optional(),
    entity_id: z.string().uuid().nullable().optional(),
    must_reset: z.boolean().optional(),
  }),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

export const MeResponse = z.object({
  // backend returns user_id, frontend legacy code may reference .id
  user_id: z.string().uuid().optional(),
  id: z.string().uuid().optional(),
  email: z.string().email(),
  role: Role,
  full_name: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  entity_id: z.string().uuid().nullable().optional(),
  entity_name: z.string().nullable().optional(),
  must_reset: z.boolean().optional(),
  is_active: z.boolean().optional(),
  tenant_config: z
    .object({
      onboarding_date: z.string().nullable().optional(),
      default_currency: z.string().optional(),
      fiscal_year_start: z.string().optional(),
    })
    .passthrough()
    .optional(),
  preferences: z
    .object({
      theme: ThemePreference.optional(),
      timezone: z.string().optional(),
      sidebar_collapsed: z.boolean().optional(),
    })
    .partial()
    .passthrough()
    .optional(),
  feature_flags: z.record(z.unknown()).optional(),
  // S-FIX-1-BE.B.1 — extended scope для FE useUserScope() hook.
  // UX18/B6 — approver_hints для DisabledWithReason tooltips.
  scope: z
    .object({
      warehouse_codes: z.array(z.string()),
      unrestricted: z.boolean(),
      abilities: z.record(z.array(z.enum(['read', 'create', 'update', 'delete', 'approve']))),
      approver_hints: z.record(z.string()).optional(),
      // access-control (B5/F2) — эффективный набор модулей активной компании.
      module_access: z.array(z.string()).optional(),
    })
    .passthrough()
    .optional(),
  // S-multi-1 BM-2-6 — Multi-tenancy fields в /auth/me.
  is_super_admin: z.boolean().optional(),
  default_entity_id: z.string().uuid().nullable().optional(),
  active_entity_id: z.string().uuid().nullable().optional(),
  /**
   * CC-7 — имя компании из `active_entity_id`. Отдельное поле, а не переиспользованное
   * `entity_name`: последнее принадлежит легаси-`entity_id`, и это РАЗНЫЕ компании,
   * когда `users.entity_id` и `users.default_entity_id` разошлись.
   */
  active_entity_name: z.string().nullable().optional(),
  effective_role: z.string().nullable().optional(),
  accessible_entity_ids: z.array(z.string()).optional(),
  // multi-tenancy-rls B2 — defense-in-depth слой (RLS) включён на бэкенде.
  // Boolean (не freeform) — источник для admin-индикатора F1.
  rls_active: z.boolean().optional(),
  /** B76 (Ф6, §9.5): деловой пояс — граница суток для «сегодня» и «просрочено». */
  business_timezone: z.string().optional(),
  memberships: z
    .array(
      z
        .object({
          source: z.enum(['direct', 'holding']),
          entity_id: z.string().uuid(),
          entity_name: z.string(),
          role: z.string(),
          holding_id: z.string().uuid().optional(),
          holding_name: z.string().optional(),
          /** B42 (T2A-F18): модули, открытые в ЭТОЙ компании, — для экрана отказа. */
          module_access: z.array(z.string()).optional(),
        })
        .passthrough(),
    )
    .optional(),
});
export type MeResponse = z.infer<typeof MeResponse>;

export const PasswordChangeRequest = z
  .object({
    current_password: z.string().min(1, { message: 'Текущий пароль обязателен' }),
    new_password: z.string().min(8, { message: 'Минимум 8 символов' }),
    confirm_password: z.string().min(1),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'Пароли не совпадают',
  });
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequest>;

export const AcceptInvitePreview = z.object({
  email: z.string().email(),
  // role — только для отображения (getRoleLabel). Приглашение может нести holding-роль
  // (holding_admin/holding_viewer), которой нет в canonical Role → strict enum ронял
  // preview («Не удалось загрузить приглашение»). z.string() = drift-proof.
  role: z.string(),
  invited_by_email: z.string().email().nullable(),
  entity_name: z.string().nullable(),
  /** invite-multi-entity — компании и роль в каждой; optional = drift-proof к старому BE. */
  memberships: z
    .array(
      z.object({
        entity_name: z.string().nullable(),
        role: z.string(),
      }),
    )
    .optional(),
  expires_at: z.string().datetime(),
});
export type AcceptInvitePreview = z.infer<typeof AcceptInvitePreview>;

export const AcceptInviteRequest = z
  .object({
    token: z.string().min(16),
    new_password: z.string().min(8, { message: 'Минимум 8 символов' }),
    confirm_password: z.string().min(1),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'Пароли не совпадают',
  });
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequest>;

export const PasswordResetRequest = z.object({
  email: z.string().email({ message: 'Введите корректный email' }),
});
export type PasswordResetRequest = z.infer<typeof PasswordResetRequest>;

export const PasswordResetConfirmRequest = z
  .object({
    token: z.string().min(16),
    new_password: z.string().min(8, { message: 'Минимум 8 символов' }),
    confirm_password: z.string().min(1),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'Пароли не совпадают',
  });
export type PasswordResetConfirmRequest = z.infer<typeof PasswordResetConfirmRequest>;

// BE-H/I Pass 79: BE возвращает legacy DB role 'expert' который не входит в canonical
// Role enum ('accountant' | 'reviewer' | 'sme' | ...). Маппим 'expert' → 'sme' (Subject
// Matter Expert — семантический эквивалент). Канонический Role не загрязняется.
//
// BM-S-multi-6-2: в исторических invite-токенах встречается роль 'member' (старый flow,
// до перехода на MembershipRole), которой нет в canonical enum. Без маппинга весь
// AdminInvitesResponse падает на Zod → /admin/users/invites = «schema drift» (incident 2026-06-17).
// Маппим 'member' → 'viewer' (минимальный доступ — безопасный эквивалент для отображения).
const LEGACY_ROLE_ALIASES: Record<string, string> = {
  expert: 'sme',
  member: 'viewer',
};
const LegacyRole = z.preprocess((val) => {
  if (typeof val === 'string' && val in LEGACY_ROLE_ALIASES) return LEGACY_ROLE_ALIASES[val];
  return val;
}, Role);

export const AdminUser = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  full_name: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  role: LegacyRole,
  is_active: z.boolean(),
  /** S-multi-1 — глобальный флаг супер-админа (god-mode в обход RBAC). Управляется
   *  только супер-админом через POST /admin/users/:id/super-admin. */
  is_super_admin: z.boolean().optional(),
  must_reset: z.boolean().optional(),
  /**
   * Поле объявлено с начала и НИКОГДА не приходило: колонки `last_login_at` в
   * базе нет. Оставлено, пока читатели не сняты, но живость аккаунта отвечает
   * `last_api_call_at` ниже (T2A-F3/SR-F2).
   */
  last_login_at: z.string().datetime().nullable().optional(),
  /** T2A-F3/SR-F2 — когда токен аккаунта последний раз дошёл до API. */
  last_api_call_at: z.string().datetime().nullable().optional(),
  /** B47 (U-2): активные сессии — семейства refresh-токенов без отзыва и истечения. */
  session_count: z.number().int().nonnegative().optional(),
  created_at: z.string().datetime(),
  /** P14.5 / G16.6 — labor cost rate для project-aggregator engine. */
  default_costing_rate: z
    .object({
      amount: z.string(),
      currency: z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']),
    })
    .nullable()
    .optional(),
  /**
   * BM-S-multi-6-4 — denorm entity memberships для pills «Компания · Роль».
   * BE отдаёт через leftJoin entities; без объявления здесь Zod вырезал поле (incident 2026-06-17).
   */
  entity_memberships: z
    .array(
      z.object({
        entity_id: z.string().uuid(),
        entity_name: z.string().nullable(),
        role: z.string(),
        status: z.string(),
      }),
    )
    .optional(),
  /**
   * B83 пака `tails-closeout` (NC-53, SOLUTION § 10.6 п. 11; К5-7) — отключение, которым
   * управляет смотрящий: админ компании — членство в текущей компании (`membership`), суперадмин
   * в «Все компании» — аккаунт (`account`); аккаунт, выключенный целиком, виден и админу
   * компании. `at = null` — дата неизвестна (до отметок). `by` — только если отключивший виден в
   * области видимости смотрящего (по активным членствам, С-I2 Р.6); иначе `by_hidden` — кем был
   * невидимый: суперадмин — `platform_admin`, иначе `other_company` (имя из чужой компании не
   * уходит). `null` — доступ не отключён.
   */
  disabled: z
    .object({
      scope: z.enum(['membership', 'account']),
      entity_id: z.string().uuid().nullable(),
      at: z.string().datetime().nullable(),
      by: z.object({ id: z.string().uuid(), name: z.string().nullable() }).nullable(),
      by_hidden: z.enum(['platform_admin', 'other_company']).nullable(),
    })
    .nullable()
    .optional(),
});
export type AdminUser = z.infer<typeof AdminUser>;

/**
 * PLM-1 (пак `tails-closeout`, B13; В-28) — оклад и производная ставка часа для
 * карточки сотрудника (`GET /admin/users/:id/compensation`). Оклад — чувствительные
 * данные: в списке пользователей его нет, только здесь, под админской ручкой.
 */
const CompensationMoney = z.object({
  amount: z.string(),
  currency: z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']),
});
export const UserCompensation = z.object({
  monthly_salary: CompensationMoney.nullable(),
  /** Ставка часа = оклад / месячная норма; только чтение. */
  hourly_rate: CompensationMoney.nullable(),
  /** Месячная норма часов (40 ч/нед × 52 / 12), строкой с двумя знаками. */
  monthly_norm_hours: z.string(),
});
export type UserCompensation = z.infer<typeof UserCompensation>;

export const AdminUsersResponse = z.object({
  items: z.array(AdminUser),
  next_cursor: z.string().nullable().optional(),
});
export type AdminUsersResponse = z.infer<typeof AdminUsersResponse>;

export const InviteUserInput = z.object({
  email: z.string().email({ message: 'Введите корректный email' }),
  role: Role,
});
export type InviteUserInput = z.infer<typeof InviteUserInput>;

export const BulkInviteResult = z.object({
  created: z.array(
    z.object({
      // BE возвращает поле `id`, не `user_id` — fix legacy schema.
      // Поддержим оба ключа для совместимости со старыми клиентами.
      user_id: z.string().uuid().optional(),
      id: z.string().uuid().optional(),
      email: z.string().email(),
      // С-H2 Р.4 — письмо уходит после коммита: `null` — в очереди, исход в ответе неизвестен.
      email_sent: z.boolean().nullable().optional(),
      email_queued: z.boolean().optional(),
      /** С-I2 Р.8 (В-50) — ссылка приглашения один раз, в итоге массового. */
      accept_url: z.string().url().optional(),
    }),
  ),
  failed: z.array(
    z.object({
      email: z.string(),
      reason: z.string(),
      /** С-I6 Р.2 (ACC-B22) — при `INSUFFICIENT_ROLE`: роль, которой не хватило. */
      required: z.string().optional(),
    }),
  ),
});
export type BulkInviteResult = z.infer<typeof BulkInviteResult>;

/**
 * С-I6 Р.2 (ACC-B22) — GET /admin/users/invite-options: компании, куда приглашающий вправе
 * приглашать, и роли не выше его роли там (по убыванию ранга); `bulk` — компания старой формы
 * массового приглашения (компания запроса), `null` — там приглашать нельзя.
 */
export const AdminInviteOptions = z
  .object({
    items: z.array(
      z
        .object({
          entity_id: z.string().uuid(),
          entity_name: z.string().nullable(),
          roles: z.array(z.string()),
        })
        .strict(),
    ),
    bulk: z
      .object({ entity_id: z.string().uuid(), roles: z.array(z.string()) })
      .strict()
      .nullable(),
  })
  .strict();
export type AdminInviteOptions = z.infer<typeof AdminInviteOptions>;

// POST /admin/users/invite response
export const InviteResponse = z.object({
  user_id: z.string().uuid(),
  expires_at: z.string().datetime(),
  // С-H2 Р.6 — письмо уходит после коммита: `null` — в очереди (`email_queued: true`), `false` —
  // прежний ответ «не ушло» (старый бэкенд в двухрелизный период).
  email_sent: z.boolean().nullable(),
  email_queued: z.boolean().optional(),
  email_error: z.string().nullable().optional(),
  /**
   * С-I2 Р.8 (TCA-B10, В-50) — ссылка приглашения отдаётся ОДИН раз, в ответе выпуска (приглашение,
   * «Отправить снова»): администратор может передать её сам, если письмо не дошло. Сервер её не
   * хранит и в списке не отдаёт.
   */
  accept_url: z.string().url().optional(),
});
export type InviteResponse = z.infer<typeof InviteResponse>;

// GET /admin/users/invites
export const InviteStatus = z.enum(['pending', 'accepted', 'expired']);
export type InviteStatus = z.infer<typeof InviteStatus>;

export const AdminInvite = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  // role — display-only (getRoleLabel). Приглашение в холдинг несёт holding-роль,
  // которой нет в LegacyRole → весь /admin/users/invites падал на Zod. z.string() = drift-proof.
  role: z.string(),
  /** invite-multi-entity — 'entity' | 'holding'; optional = drift-proof к старому BE. */
  target_kind: z.string().optional(),
  /** invite-multi-entity — компании инвайта с ролью в каждой (пусто для holding). */
  memberships: z
    .array(
      z.object({
        entity_id: z.string().uuid(),
        entity_name: z.string().nullable(),
        role: z.string(),
      }),
    )
    .optional(),
  status: InviteStatus,
  invited_by_email: z.string().email().nullable(),
  invited_at: z.string().datetime(),
  expires_at: z.string().datetime(),
  accepted_at: z.string().datetime().nullable(),
  /**
   * B84 пака `tails-closeout` (TCA-B8, § 10.6 п. 15, В-47 «а») — письмо приглашения у
   * действующего токена: `sent` — принято почтовым сервисом (не «доставлено в ящик»), `failed` —
   * не ушло после трёх попыток, `queued` — отправляется, `unknown` — отправка дольше 15 минут
   * или токен до учёта статуса. `null` — токен принят или истёк, письмо не показывается.
   */
  email_delivery: z.enum(['queued', 'sent', 'failed', 'unknown']).nullable().optional(),
  email_status_at: z.string().datetime().nullable().optional(),
  /** С-I2 Р.8 (В-50) — погашен повтором «Отправить снова» (серверный признак), иначе null. */
  replaced_at: z.string().datetime().nullable().optional(),
});
export type AdminInvite = z.infer<typeof AdminInvite>;

export const AdminInvitesResponse = z.object({
  items: z.array(AdminInvite),
});
export type AdminInvitesResponse = z.infer<typeof AdminInvitesResponse>;

export const ImpersonationSession = z.object({
  active: z.boolean(),
  target_user_email: z.string().email().nullable(),
  started_at: z.string().datetime().nullable(),
});
export type ImpersonationSession = z.infer<typeof ImpersonationSession>;

// ─── Integrations ────────────────────────────────────────────────────

// Legacy sync-mode, 4-valued — используется в UI-форме Integration (admin).
// Канонический `IntegrationSyncMode` из enums.js — 2-valued ['read','read_write'].
// BE-G Pass 79 (2026-05-25): BE возвращает canonical 'read'/'read_write' + legacy
// DB-drift 'two_way'/'write' — preprocess маппит в legacy UI shape.
export const IntegrationSyncModeLegacy = z.preprocess(
  (val) => {
    if (typeof val !== 'string') return val;
    // BE → UI legacy mapping.
    switch (val) {
      case 'read':
        return 'pull';
      case 'write':
        return 'push';
      case 'read_write':
      case 'two_way':
        return 'bidirectional';
      default:
        return val;
    }
  },
  z.enum(['pull', 'push', 'bidirectional', 'disabled']),
);
export type IntegrationSyncModeLegacy = z.infer<typeof IntegrationSyncModeLegacy>;

export const IntegrationCredentialField = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['text', 'password', 'url']),
  required: z.boolean().default(true),
  masked_value: z.string().nullable().optional(),
});
export type IntegrationCredentialField = z.infer<typeof IntegrationCredentialField>;

/**
 * S-FIX-9-FE §7-tris: relax schema — BE возвращает `is_enabled`, `config` (jsonb)
 * vs legacy FE expecting `name`, `schedule_cron`, `onboarding_date`, `message`,
 * `credentials[]`. Делаем optional + passthrough чтобы UI рендерил что есть,
 * а не падал на «Несовместимый ответ сервера».
 */
// INT-DRAWER-2: per-entity scope toggles. All optional — old BE может не возвращать.
export const IntegrationScope = z
  .object({
    items: z.boolean().default(true),
    warehouses: z.boolean().default(true),
    boms: z.boolean().default(true),
    counterparties: z.boolean().default(true),
    batches: z.boolean().default(true),
  })
  .strict();
export type IntegrationScope = z.infer<typeof IntegrationScope>;

export const Integration = z
  .object({
    id: z.string().uuid(),
    peer: z.enum(['1c', 'bitrix', 'adesk']),
    name: z.string().optional(),
    health: z.enum(['ok', 'degraded', 'disconnected']),
    sync_mode: IntegrationSyncModeLegacy,
    schedule_cron: z.string().nullable().optional(),
    onboarding_date: z.string().date().nullable().optional(),
    last_pull_at: z.string().datetime().nullable().optional(),
    last_push_at: z.string().datetime().nullable().optional(),
    last_attempt_at: z.string().datetime().nullable().optional(),
    message: z.string().nullable().optional(),
    credentials: z.array(IntegrationCredentialField).default([]).optional(),
    is_enabled: z.boolean().optional(),
    config: z.unknown().optional(),
    scope: IntegrationScope.optional(),
    created_at: z.string().datetime().optional(),
    updated_at: z.string().datetime().optional(),
  })
  .passthrough();
export type Integration = z.infer<typeof Integration>;

export const IntegrationsResponse = z.object({
  items: z.array(Integration),
});
export type IntegrationsResponse = z.infer<typeof IntegrationsResponse>;

export const IntegrationHealthEventLegacy = z.object({
  id: z.string(),
  integration_id: z.string().uuid(),
  health: z.enum(['ok', 'degraded', 'disconnected']),
  message: z.string().nullable(),
  occurred_at: z.string().datetime(),
});
export type IntegrationHealthEventLegacy = z.infer<typeof IntegrationHealthEventLegacy>;

export const IntegrationHealthHistoryResponse = z.object({
  items: z.array(IntegrationHealthEventLegacy),
});
export type IntegrationHealthHistoryResponse = z.infer<typeof IntegrationHealthHistoryResponse>;

// ─── Dashboard (stub shape preserved) ────────────────────────────────

// BE-1 (2026-05-20): дублирующий enum заменён на reuse `RunBusinessStatus`
// из enums.ts (canonical BusinessStatus ∪ lifecycle states).
export const BusinessStatusLegacyNullable = RunBusinessStatus.nullable();

// S-FIX-4 consolidation: was a DUPLICATE inline schema that drifted from
// api-contracts.RunSummary (caused S-FIX-3 hotfix-3/4 missing one of two).
// Now references the single canonical RunSummary export.
import { RunSummary } from './api-contracts.js';
export const RunListResponse = z.object({
  items: z.array(RunSummary),
  next_cursor: z.string().nullable().optional(),
});
export type RunListResponse = z.infer<typeof RunListResponse>;

export const DashboardSummary = z.object({
  open_conflicts: z.object({ count: z.number().int(), delta_24h: z.number().int() }),
  pending_approvals: z.object({
    count: z.number().int(),
    oldest_age_hours: z.number().nullable(),
  }),
  last_run: z
    .object({
      run_id: z.string().uuid(),
      finished_at: z.string().datetime(),
      business_status: BusinessStatusLegacyNullable,
    })
    .nullable(),
  recent_runs: z
    .array(
      z.object({
        id: z.string().uuid(),
        task_type: TaskType,
        runtime_status: z.string(),
        business_status: BusinessStatusLegacyNullable,
        period: z.object({ date_from: z.string().date(), date_to: z.string().date() }),
        started_at: z.string().datetime(),
        finished_at: z.string().datetime().nullable(),
        created_by: z.string().optional(),
      }),
    )
    .max(5),
  anomalies_trend_7d: z
    .array(z.object({ day: z.string().date(), count: z.number().int() }))
    .default([]),
});
export type DashboardSummary = z.infer<typeof DashboardSummary>;

// ─── Conflicts UI (legacy shape) ─────────────────────────────────────

/**
 * Legacy EvidenceRef — UI-shape (source / document_id / fetched_at).
 * Канонический EvidenceRef (entities) — иная форма (source_system / source_document_id / pointer).
 */
export const EvidenceRefLegacy = z.object({
  id: z.string(),
  source: z.enum(['staiter', '1c', 'bitrix', 'adesk', 'excel', 'manual']),
  document_id: z.string().optional(),
  label: z.string().optional(),
  fetched_at: z.string().datetime().optional(),
});
export type EvidenceRefLegacy = z.infer<typeof EvidenceRefLegacy>;

export const ConflictSide = z.object({
  source: z.enum(['staiter', '1c', 'bitrix', 'adesk', 'excel', 'manual']),
  level: SourceLevel.optional(),
  value: z.union([z.string(), z.number()]),
  document_id: z.string().optional(),
  fetched_at: z.string().datetime().optional(),
  label: z.string().optional(),
});
export type ConflictSide = z.infer<typeof ConflictSide>;

/**
 * Legacy SourceConflict UI shape — UI-вариант с rich полями (subject, source_a/b,
 * diff_label). Отличается от canonical `SourceConflict` в entities (который сохраняется
 * в БД — subject_kind/subject_ref/sources[]). Используется ConflictCard и route-ами.
 */
export const SourceConflictLegacy = z.object({
  id: z.string(),
  type: z.enum([
    'intra_level',
    'master_data_drift',
    'bom_explosion_loop',
    'valuation_mismatch',
    'temporal_precedence_unresolved',
    'reconciliation_variance',
    'tolerance_exceeded',
    'manual_override_deferred',
  ]),
  severity: ConflictSeverity,
  subject: z.string(),
  source_a: ConflictSide,
  source_b: ConflictSide,
  authoritative: z.enum(['a', 'b']).optional(),
  diff_label: z.string().optional(),
});
export type SourceConflictLegacy = z.infer<typeof SourceConflictLegacy>;

export const ConflictTypeLegacy = z.enum([
  'intra_level',
  'master_data_drift',
  'bom_explosion_loop',
  'valuation_mismatch',
  'temporal_precedence_unresolved',
  'reconciliation_variance',
  'tolerance_exceeded',
  'manual_override_deferred',
]);
export type ConflictTypeLegacy = z.infer<typeof ConflictTypeLegacy>;

// ─── Period-close checklist ──────────────────────────────────────────

export const PeriodCloseBlocker = z.object({
  kind: PeriodCloseBlockerKind,
  count: z.number().int().nonnegative(),
  label: z.string(),
  deeplink: z.string().optional(),
});
export type PeriodCloseBlocker = z.infer<typeof PeriodCloseBlocker>;

export const PeriodCloseChecklistResponse = z.object({
  period: z.string(),
  state: PeriodLockState,
  locked_at: z.string().datetime().nullable(),
  locked_by: z.string().nullable(),
  can_lock: z.boolean(),
  blockers: z.array(PeriodCloseBlocker).default([]),
  audit_trail: z
    .array(
      z.object({
        at: z.string().datetime(),
        actor: z.string(),
        action: z.string(),
        note: z.string().optional(),
      }),
    )
    .default([]),
});
export type PeriodCloseChecklistResponse = z.infer<typeof PeriodCloseChecklistResponse>;

// ─── Approvals (legacy richer shape) ─────────────────────────────────

// BE-C Pass 87: approver_name nullable (resolveUsers может fail); role через LegacyRole
// (BE возвращает 'reviewer'/'admin'/'expert'/'sme' и т.д.).
export const ApprovalSignatureLegacy = z.object({
  approver_id: z.string().uuid(),
  approver_name: z.string().nullable(),
  role: LegacyRole,
  signed_at: z.string().datetime(),
  note: z.string().optional(),
});
export type ApprovalSignatureLegacy = z.infer<typeof ApprovalSignatureLegacy>;

export const ApprovalItem = z.object({
  id: z.string().uuid(),
  task_type: z.string(),
  tier: ApprovalTier,
  status: ApprovalStatus,
  // S-FIX-4: BE handler doesn't always populate these (legacy approval rows).
  // BE-A Pass 81: BE возвращает `title` field — добавлено как optional alias subject.
  title: z.string().optional(),
  subject: z.string().optional(),
  run_id: z.string().uuid().optional(),
  entity_type: z.string().optional(),
  entity_id: z.string().optional(),
  requested_by: z.string().optional(),
  requested_at: z.string().datetime().optional(),
  age_hours: z.number().nonnegative().optional(),
  // FE-14 fix (2026-05-31): canonical BE field name is `age_h` (api-contracts.ts:952).
  // Legacy schema must declare it explicitly — Zod strips unknown keys by default,
  // so without this `age_h` from BE response is lost → slaLevel(0) → always 'ok' pill.
  age_h: z.number().nonnegative().optional(),
  sla_flag: z.enum(['ok', 'yellow', 'red']).optional(),
  /** B40 (T2A-F16): ждёт дольше нормы (три рабочих дня). */
  overdue_norm: z.boolean().optional(),
  /** B40 (F30): когда решающему напомнили в последний раз; лимит — раз в сутки. */
  last_reminded_at: z.string().datetime().nullable().optional(),
  /**
   * B41 (T2A-F17): маршрут — кто решает и на каком шаге заявка. `role` — код роли
   * (подпись — через словарь ролей), `alt_roles` — «любой из» у однаподписного шага.
   */
  route: z
    .array(
      z.object({
        step: z.number().int().positive(),
        role: z.string().nullable(),
        alt_roles: z.array(z.string()),
        user_id: z.string().nullable(),
        user_name: z.string().nullable(),
        status: z.enum(['done', 'current', 'pending', 'rejected', 'skipped']),
      }),
    )
    .optional(),
  current_step: z.number().int().positive().nullable().optional(),
  required_signatures: z.number().int().min(1).max(3).optional(),
  collected_signatures: z.array(ApprovalSignatureLegacy).default([]),
  /**
   * B5/F3 (tier2-approval-requests §5) — адресность заявки.
   *
   * Без этих полей экран очереди не может отличить «моё» от «чужого», а именно
   * ради именной ответственности пак и затевался.
   */
  requested_by_user_id: z.string().uuid().nullable().optional(),
  addressee_user_id: z.string().uuid().nullable().optional(),
  /**
   * Имена сторон приходят вместе с карточкой, а не только денорм-набором списка.
   *
   * Заход 4 гейта: инициатор попадает в заявку ссылкой с карточки проекта, минуя
   * список. Денорм-набор `users` в этот момент пуст, и без этих двух полей экран
   * называл человека сокращённым идентификатором — то есть не называл вовсе.
   *
   * Zod по умолчанию СРЕЗАЕТ необъявленные ключи: ручка отдавала `requested_by_name`,
   * а карточка продолжала показывать `eeee0000…` — поле, которого нет в схеме, до
   * экрана не доезжает, сколько бы его ни слал сервер.
   */
  requested_by_name: z.string().nullable().optional(),
  addressee_name: z.string().nullable().optional(),
  expires_at: z.string().datetime().nullable().optional(),
  /** Тело намерения: что именно произойдёт при согласовании (F4). */
  payload: z.record(z.unknown()).optional(),
  /**
   * Снимок последствий на момент СОЗДАНИЯ заявки (заход 4 гейта).
   *
   * Решающий обязан знать о последствиях не меньше просящего, а живое превью ему
   * недоступно: оно живёт под модулем `projects`, которого у роли `reviewer` нет.
   */
  impact: z.record(z.number()).nullable().optional(),
  /** B39 (T2A-F14): живой расчёт последствий и признак расхождения со снимком. */
  impact_now: z.record(z.number()).nullable().optional(),
  impact_changed: z.boolean().optional(),
  /** F28 (T2A-F14) — когда снимок последствий пересобран; null — не пересобирался. */
  impact_rebuilt_at: z.string().datetime().nullable().optional(),
  subject_id: z.string().optional(),
  subject_kind: z.string().optional(),
  /**
   * F92 пака `tails-closeout` (Ф6, SOLUTION §9.4 п. 9) — заявка уровня 3: шкала подписей с
   * `signed_at`, состояние документа сейчас, «документ изменился после подписи» и подключена
   * ли 1С (без неё вторая подпись не ставится — «исполнится после подключения 1С»).
   */
  tier3: z
    .object({
      signatures: z.array(
        z.object({
          user_id: z.string().uuid(),
          name: z.string().nullable(),
          role: z.string(),
          zone: z.string(),
          signed_at: z.string().datetime(),
        }),
      ),
      /** Сводка на момент подписи №1 (документы, суммы, период, крайний срок). */
      snapshot: z
        .object({
          documents: z
            .array(
              z.object({
                id: z.string(),
                kind: z.string(),
                number: z.string().nullable(),
                state: z.string().nullable(),
              }),
            )
            .default([]),
          documents_total: z.number().int().nonnegative().default(0),
          amounts: z.array(z.object({ amount: z.string(), currency: z.string() })).default([]),
          period: z.string().nullable().default(null),
          deadline: z.string().nullable().default(null),
        })
        .passthrough()
        .nullable(),
      subject_state_now: z.string().nullable(),
      subject_missing: z.boolean(),
      stale: z.boolean(),
      integration_connected: z.boolean(),
    })
    .optional(),
  /**
   * Кто и когда решил. Поля приходили от сервера и молча терялись: Zod вырезает
   * необъявленные ключи, и колонка «Решение» в истории показывала прочерк на
   * решённой заявке — «Согласовано» без ответа на «кем и когда».
   */
  decided_by_user_id: z.string().uuid().nullable().optional(),
  decided_at: z.string().datetime().nullable().optional(),
  decision_reason: z.string().nullable().optional(),
  diff: z
    .object({
      before: z.record(z.unknown()),
      after: z.record(z.unknown()),
    })
    .optional(),
  evidence_refs: z
    .array(
      z.object({
        id: z.string(),
        source: z.string(),
        document_id: z.string().optional(),
        label: z.string().optional(),
        fetched_at: z.string().datetime().optional(),
      }),
    )
    .default([]),
  sla: z
    .object({
      warning_after_hours: z.number().nonnegative(),
      critical_after_hours: z.number().nonnegative(),
    })
    .optional(),
});
export type ApprovalItem = z.infer<typeof ApprovalItem>;

export const ApprovalListResponse = z.object({
  items: z.array(ApprovalItem),
  next_cursor: z.string().nullable().optional(),
});
export type ApprovalListResponse = z.infer<typeof ApprovalListResponse>;

// BE-I Pass 79: BE возвращает `min_signatures` + 'expert' role. Используем LegacyRole
// + preprocess для маппинга min_signatures → min_signers (BE field rename).
export const ApprovalMatrixEntry = z
  .object({
    id: z.string().uuid(),
    task_type: z.string(),
    tier: z.number().int().min(0).max(3),
    required_roles: z.array(LegacyRole),
    min_signers: z.number().int().min(1).optional(),
    min_signatures: z.number().int().min(1).optional(),
    description: z.string().nullable().optional(),
  })
  .transform((v) => ({
    ...v,
    min_signers: v.min_signers ?? v.min_signatures ?? 1,
  }));
export type ApprovalMatrixEntry = z.infer<typeof ApprovalMatrixEntry>;

export const ApprovalMatrixResponse = z.object({
  items: z.array(ApprovalMatrixEntry),
});
export type ApprovalMatrixResponse = z.infer<typeof ApprovalMatrixResponse>;

// ─── Audit log legacy list ───────────────────────────────────────────

// S-FIX-4: BE list handler doesn't always populate actor/resource (legacy rows).
export const AuditEventLegacy = z.object({
  id: z.string(),
  occurred_at: z.string().datetime(),
  actor: z.string().optional(),
  action: z.string(),
  resource: z.string().optional(),
  entity_type: z.string().optional(),
  entity_id: z.string().optional(),
  status: z.enum(['ok', 'error']).default('ok'),
  latency_ms: z.number().int().nullable().optional(),
  trace_id: z.string().nullable().optional(),
  input_hash: z.string().nullable().optional(),
  output_hash: z.string().nullable().optional(),
  payload: z.record(z.unknown()).default({}),
});
export type AuditEventLegacy = z.infer<typeof AuditEventLegacy>;

export const AuditListResponse = z.object({
  items: z.array(AuditEventLegacy),
  next_cursor: z.string().nullable().optional(),
});
export type AuditListResponse = z.infer<typeof AuditListResponse>;

// ─── Memory governance ───────────────────────────────────────────────

export const MemoryEntry = z.object({
  id: z.string(),
  scope: MemoryScope,
  lifecycle: MemoryLifecycle,
  sensitivity: MemorySensitivity,
  provenance: z.string(),
  fresh_until: z.string().datetime().nullable(),
  version: z.number().int().nonnegative(),
  summary: z.string(),
  updated_at: z.string().datetime(),
});
export type MemoryEntry = z.infer<typeof MemoryEntry>;

export const MemoryListResponse = z.object({
  items: z.array(MemoryEntry),
  stats: z
    .object({
      by_scope: z.record(z.number().int()).default({}),
      avg_age_days: z.number().nullable().default(null),
      recall_hit_rate: z.number().nullable().default(null),
    })
    .default({ by_scope: {}, avg_age_days: null, recall_hit_rate: null }),
});
export type MemoryListResponse = z.infer<typeof MemoryListResponse>;

// ─── User preferences + saved views ──────────────────────────────────

/**
 * Per-user sidebar layout (2026-06-08).
 *
 * Хранит collapse / reorder / rename / custom sections / per-item override + ordering.
 * Сохраняется в `user_preferences[sidebar_layout]` как полный snapshot — debounced
 * upsert при изменениях на фронте, hydrate при логине. localStorage остаётся
 * offline-кэшем (zustand persist), серверная версия — source of truth.
 */
export const SidebarLayoutPref = z.object({
  sectionsCollapsed: z.record(z.boolean()).default({}),
  sectionOrder: z.array(z.string()).default([]),
  sectionTitles: z.record(z.string()).default({}),
  customSections: z.array(z.object({ id: z.string(), title: z.string() })).default([]),
  itemSectionOverrides: z.record(z.string()).default({}),
  itemOrder: z.record(z.array(z.string())).default({}),
});
export type SidebarLayoutPref = z.infer<typeof SidebarLayoutPref>;

export const UserPreferences = z.object({
  theme: ThemePreference.default('system'),
  sidebar_collapsed: z.boolean().default(false),
  timezone: z.string().default('Asia/Almaty'),
  density: z.enum(['compact', 'comfortable']).default('comfortable'),
  default_run_task_type: TaskType.nullable().default(null),
  // SET-AUDIT-5 (pass3 settings audit) — granular notification toggles.
  notifications_email_approvals: z.boolean().default(true),
  notifications_email_conflicts: z.boolean().default(true),
  notifications_email_anomalies: z.boolean().default(true),
  notifications_digest_cadence: z.enum(['immediate', 'hourly', 'daily']).default('immediate'),
  // 2026-06-08 — per-user sidebar layout.
  sidebar_layout: SidebarLayoutPref.optional(),
});
export type UserPreferences = z.infer<typeof UserPreferences>;

export const SavedViewServer = z.object({
  id: z.string().uuid(),
  name: z.string(),
  resource: z.string(),
  filters: z.record(z.unknown()),
  is_shared: z.boolean().optional(),
  is_default: z.boolean().optional(),
  owner_id: z.string().uuid().optional(),
  created_at: z.string().datetime(),
});
export type SavedViewServer = z.infer<typeof SavedViewServer>;

export const SavedViewsResponse = z.object({
  items: z.array(SavedViewServer),
});
export type SavedViewsResponse = z.infer<typeof SavedViewsResponse>;

// ─── Chat (legacy richer shape — overrides canonical ChatEvent naming) ─

export const ChatContext = z.object({
  run_id: z.string().uuid().optional(),
  period: z.string().optional(),
  scope: z.record(z.unknown()).optional(),
  route: z.string().optional(),
  // CH-7 — role используется backend'ом для выбора role-specific user-guide
  // документации (см. apps/backend/src/llm/docs-corpus.ts pickRelevantDocs).
  // Optional: старые клиенты могут не слать; backend fallback на user.role из JWT.
  // Backend валидирует совпадение с JWT (anti-spoof).
  role: Role.optional(),
});
export type ChatContext = z.infer<typeof ChatContext>;

/**
 * Legacy ChatMessageRow (UI-shape) — содержит text/tool_name/evidence_refs.
 * Канонический ChatMessageRow (excel-contracts) — минимальный (content/tool_calls).
 */
export const ChatMessageRowLegacy = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  role: z.enum(['user', 'assistant', 'system', 'tool']),
  text: z.string(),
  created_at: z.string().datetime(),
  reasoning_hint: z.string().optional(),
  tool_name: z.string().optional(),
  tool_args_hash: z.string().optional(),
  evidence_refs: z
    .array(
      z.object({
        id: z.string(),
        source: z.string(),
        document_id: z.string().optional(),
        label: z.string().optional(),
        fetched_at: z.string().datetime().optional(),
      }),
    )
    .default([]),
});
export type ChatMessageRowLegacy = z.infer<typeof ChatMessageRowLegacy>;

export const ChatConversationRowLegacy = z.object({
  id: z.string().uuid(),
  title: z.string(),
  created_at: z.string().datetime(),
  // BE handler не всегда возвращает updated_at (prod-снимок 2026-05-16 trace 62c30671).
  // Tolerant до S-FIX-6 BE-audit / proper UpdatedAt-trigger.
  updated_at: z.string().datetime().optional(),
  last_message_preview: z.string().nullable().optional(),
});
export type ChatConversationRowLegacy = z.infer<typeof ChatConversationRowLegacy>;

/**
 * Legacy ChatEvent — UI-shape (discriminator 'type', не 'kind').
 * Канонический ChatEvent (events.ts) использует kind с chat.* префиксом.
 */
export const ChatEventLegacy = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('conversation_started'),
    conversation_id: z.string().uuid(),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('message_started'),
    message_id: z.string().uuid(),
    role: z.enum(['user', 'assistant', 'system', 'tool']),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('token'),
    message_id: z.string().uuid(),
    delta: z.string(),
  }),
  z.object({
    type: z.literal('tool_call'),
    message_id: z.string().uuid(),
    tool: z.string(),
    args_hash: z.string().optional(),
    /**
     * CIA-F17 — аргументы шага. Кадр `chat.tool_call` несёт их с самого начала
     * (`trimToolArgsForStream`, длинные списки срезаны до счётчика), но мост на
     * фронте их выбрасывал, и лента отвечала «сходил 4 раза», не отвечая «куда».
     * Здесь они только ПРОЕЗЖАЮТ до ленты; контракт кадра не меняется.
     */
    args: z.record(z.unknown()).optional(),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('tool_result'),
    message_id: z.string().uuid(),
    tool: z.string(),
    summary: z.string().optional(),
    /**
     * chat-interactive-actions F2 [Л5в] — инструмент ответил ошибкой (I8).
     * Optional: старые кадры поля не несут, а его отсутствие означает успех —
     * так вело себя всё, что писалось до пака.
     */
    ok: z.boolean().optional(),
    /**
     * CIA-F19 (пак `tails-closeout`, F6) — структурный код отказа шага
     * (`tool_execution_failed`, `invalid_arguments`, …): лента называет причину
     * по коду через `@staiter/i18n`, а не «шаг не выполнен» без объяснения.
     */
    error_code: z.string().optional(),
    /**
     * Круг 2 кадра 2 гейта захода С2 (UX-1): сколько строк вернул шаг. Число, а
     * не фраза: подпись и склонение — на стороне интерфейса.
     */
    result_count: z.number().int().nonnegative().optional(),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('message_completed'),
    message_id: z.string().uuid(),
    /**
     * CIA-F25 (пак `tails-closeout`, F6) — исход хода из `chat.final.outcome` (B11).
     * Тот же набор, что `ChatTurnOutcome` в `events.ts`; перечислен здесь, а не
     * импортирован: связка `legacy-ui-contracts ↔ api-contracts` уже держит
     * латентный цикл импортов, новых рёбер в него не добавляем.
     */
    outcome: z
      .enum([
        'answered',
        'no_answer',
        'provider_error',
        'tool_budget_exhausted',
        'refused',
        'truncated',
        'aborted',
      ])
      .optional(),
    /** `X-Trace-Id` ответа потока — для пузыря провала хода. */
    trace_id: z.string().optional(),
    /**
     * Пак `llm-haiku-5-5`, F1 — код ошибки провайдера модели (`provider_rate_limited`,
     * `provider_overloaded`, `provider_auth`, `provider_billing`, B5; `provider_config`, LH-B11):
     * ход закончился сбоем
     * провайдера, и лента рисует запись провала хода с причиной по коду, а не плашку ошибки.
     */
    provider_code: z.string().optional(),
    reasoning_hint: z.string().optional(),
    evidence_refs: z
      .array(
        z.object({
          id: z.string(),
          source: z.string(),
          document_id: z.string().optional(),
          label: z.string().optional(),
          fetched_at: z.string().datetime().optional(),
        }),
      )
      .default([]),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('write_intent_refused'),
    message_id: z.string().uuid(),
    suggested_route: z.string().optional(),
    at: z.string().datetime(),
  }),
  z.object({
    type: z.literal('error'),
    message: z.string(),
    at: z.string().datetime(),
  }),
]);
export type ChatEventLegacy = z.infer<typeof ChatEventLegacy>;

export const ChatConversationsResponse = z.object({
  items: z.array(ChatConversationRowLegacy),
});
export type ChatConversationsResponse = z.infer<typeof ChatConversationsResponse>;

export const ChatHistoryResponse = z.object({
  conversation: ChatConversationRowLegacy,
  messages: z.array(ChatMessageRowLegacy),
});
export type ChatHistoryResponse = z.infer<typeof ChatHistoryResponse>;

// ─── Stock Reconciliation (legacy doc-shaped) ────────────────────────

/**
 * S-FIX-9-FE §7 — SYNC §1.2: variance_reason hybrid — расширенный enum +
 * freeform fallback (для legacy русский текст в БД до миграции).
 * Schema accepts либо enum value, либо любую строку (passthrough).
 */
export const StockReconciliationLineLegacy = z
  .object({
    item_id: z.string().uuid(),
    item_label: z.string().optional(),
    warehouse_id: z.string().uuid(),
    batch_no: z.string().nullable(),
    expected_qty: z.string(),
    physical_qty: z.string(),
    variance_qty: z.string(),
    // Variant: accept enum OR freeform string (для прод-данных с русским текстом).
    // Будет mig'ом в proper enum в Phase 2.4 BE migration (deferred).
    variance_reason: z.string().nullable(),
    variance_reason_notes: z.string().nullable().optional(),
    uom: z.string(),
  })
  .passthrough();
export type StockReconciliationLineLegacy = z.infer<typeof StockReconciliationLineLegacy>;

export const StockReconciliationDoc = z.object({
  id: z.string().uuid(),
  // BE-D Pass 84: voucher_no + warehouse_name/code denorm в BE list response.
  voucher_no: z.string().nullable().optional(),
  kind: StockReconciliationKind,
  period: z.string().optional(),
  warehouse_id: z.string().uuid().nullable().optional(),
  warehouse_name: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  status: z.enum(['draft', 'submitted', 'cancelled']),
  lines: z.array(StockReconciliationLineLegacy).default([]),
  // BE-8 follow-up: после migration 0039 scrubbed notes — поле может быть null.
  notes: z.string().nullable().optional(),
});
export type StockReconciliationDoc = z.infer<typeof StockReconciliationDoc>;

export const StockReconciliationDraft = z.object({
  kind: StockReconciliationKind,
  period: z.string().optional(),
  warehouse_id: z.string().uuid(),
  lines: z.array(
    StockReconciliationLineLegacy.pick({
      item_id: true,
      warehouse_id: true,
      batch_no: true,
      expected_qty: true,
      physical_qty: true,
      variance_reason: true,
      uom: true,
    }).extend({ variance_qty: z.string().optional() }),
  ),
  notes: z.string().optional(),
});
export type StockReconciliationDraft = z.infer<typeof StockReconciliationDraft>;

// ─── SLE preview / cancel preview (legacy) ───────────────────────────

export const SlePreviewRow = z.object({
  item_id: z.string().uuid(),
  item_label: z.string().optional(),
  warehouse_id: z.string().uuid(),
  warehouse_label: z.string().optional(),
  batch_no: z.string().nullable(),
  qty_delta: z.string(),
  valuation_rate: z.string().nullable(),
  value_delta: z.string().nullable(),
});
export type SlePreviewRow = z.infer<typeof SlePreviewRow>;

export const SlePreviewResponse = z.object({
  entry_id: z.string().uuid(),
  action: z.enum(['submit', 'cancel']),
  rows: z.array(SlePreviewRow).default([]),
  warnings: z.array(z.string()).default([]),
});
export type SlePreviewResponse = z.infer<typeof SlePreviewResponse>;

/**
 * Предпросмотр проведения складского документа.
 *
 * F1 (пак stock-entry-wizard) — схема приведена к ФАКТИЧЕСКОЙ форме ручки
 * `GET /stock-entries/:id/submit-preview`. Поймано живьём на гейте A: разбор
 * падал на первом же поле (`entry_id: Required`), потому что схема расходилась
 * с ручкой ТРЕМЯ способами сразу — `entry_id` против `stock_entry_id`, `rows`
 * против `expected_sle`, и обязательный `message` в предупреждении, которого
 * бэкенд не шлёт с тех пор, как правило №19 запретило freeform-текст в
 * UI-схемах.
 *
 * Это ровно тот же класс, что и главная находка пака (WHS-F1): контракт
 * объявлен, обе стороны ему не следуют, и никто не замечает, пока не пройдёт
 * путь руками. Правится схема, а не ручка: ручку читают два экрана, и её форма
 * — та, что уже работает в проде.
 */
export const StockEntryExpectedSle = z.object({
  item_id: z.string().uuid(),
  warehouse_id: z.string().uuid(),
  batch_id: z.string().uuid().nullable(),
  qty_change: z.string(),
  valuation_rate: z.object({ amount: z.string(), currency: z.string() }),
  balance_qty_after: z.string(),
  balance_value_after: z.object({ amount: z.string(), currency: z.string() }),
  /** F36 (WHS-F36) — изменение стоимости строки и подписи вместо UUID. */
  value_change: z.object({ amount: z.string(), currency: z.string() }).optional(),
  item_name: z.string().nullable().optional(),
  item_sku: z.string().nullable().optional(),
  warehouse_name: z.string().nullable().optional(),
  warehouse_code: z.string().nullable().optional(),
  batch_no: z.string().nullable().optional(),
});
export type StockEntryExpectedSle = z.infer<typeof StockEntryExpectedSle>;

export const StockEntrySubmitPreview = z.object({
  stock_entry_id: z.string().uuid(),
  expected_sle: z.array(StockEntryExpectedSle).default([]),
  /**
   * Предупреждение несёт только структурные поля: код типа и ссылки на
   * предмет. Текст собирает фронт — правило №19.
   */
  warnings: z
    .array(
      z.object({
        type: z.string(),
        severity: z.enum(['info', 'warn', 'danger']).default('warn'),
        blocking: z.boolean().default(false),
        related_item_id: z.string().uuid().optional(),
        related_batch_id: z.string().uuid().optional(),
      }),
    )
    .default([]),
  negative_stock_violations: z
    .array(
      z.object({
        item_id: z.string().uuid(),
        item_label: z.string().optional(),
        warehouse_id: z.string().uuid(),
        warehouse_label: z.string().optional(),
        current_qty: z.string(),
        required_qty: z.string(),
        allow_negative: z.boolean(),
      }),
    )
    .default([]),
  generated_at: z.string().optional(),
});
export type StockEntrySubmitPreview = z.infer<typeof StockEntrySubmitPreview>;

/**
 * F1 — обязательный `message` убран: бэкенд его не шлёт с тех пор, как
 * правило №19 запретило свободный текст в UI-схемах, а `severity` он отдаёт
 * значением `critical`, которого в прежнем перечислении не было. Схема
 * требовала ровно того, чего ручка не даёт, и разбор падал всегда.
 */
export const SleCancelPreviewWarning = z.object({
  type: z.enum([
    'negative_stock_after_cancel',
    'closed_period',
    'downstream_locked',
    'valuation_recompute_heavy',
    'other',
  ]),
  severity: z.enum(['info', 'warn', 'danger', 'critical']).default('warn'),
  blocking: z.boolean().default(false),
  params: z.record(z.unknown()).optional(),
});
export type SleCancelPreviewWarning = z.infer<typeof SleCancelPreviewWarning>;

export const ValuationRecomputeRow = z.object({
  sle_id: z.string().uuid(),
  item_id: z.string().uuid(),
  item_label: z.string().optional(),
  warehouse_id: z.string().uuid(),
  posting_at: z.string().datetime(),
  prev_rate: z.string(),
  new_rate: z.string(),
  delta_value: z.string(),
});
export type ValuationRecomputeRow = z.infer<typeof ValuationRecomputeRow>;

/** F1 — имена полей приведены к ручке: `type`/`id`, а не `doc_type`/`doc_id`. */
export const AffectedDocument = z.object({
  type: z.enum(['stock_entry', 'work_order', 'landed_cost', 'stock_reconciliation']),
  id: z.string().uuid(),
  recompute_required: z.boolean().default(false),
  doc_label: z.string().optional(),
});
export type AffectedDocument = z.infer<typeof AffectedDocument>;

/**
 * F1 (пак stock-entry-wizard) — схема приведена к ФАКТИЧЕСКОЙ форме ручки
 * `GET /stock-entries/:id/cancel-preview`.
 *
 * Пятый и последний слой той же поломки контура (см. `StockEntryCreateResult`,
 * `StockEntrySubmitPreview`, `StockEntrySubmitResult`): схема требовала
 * `entry_id` и `doc_type`, которых ручка не отдаёт вовсе, и звала строки
 * `compensating_rows` вместо `compensating_sle`. Диалог отмены показывал
 * «Несовместимый ответ сервера» на КАЖДОМ открытии — проверено живьём.
 */
export const SleCompensatingRow = z.object({
  item_id: z.string().uuid(),
  warehouse_id: z.string().uuid(),
  batch_id: z.string().uuid().nullable(),
  qty_change: z.string(),
  valuation_rate: z.object({ amount: z.string(), currency: z.string() }),
  posting_datetime: z.string(),
});
export type SleCompensatingRow = z.infer<typeof SleCompensatingRow>;

export const SleCancelPreviewResponse = z.object({
  stock_entry_id: z.string().uuid(),
  compensating_sle: z.array(SleCompensatingRow).default([]),
  valuation_recompute: z.array(ValuationRecomputeRow).default([]),
  affected_documents: z.array(AffectedDocument).default([]),
  warnings: z.array(SleCancelPreviewWarning).default([]),
  generated_at: z.string().optional(),
});
export type SleCancelPreviewResponse = z.infer<typeof SleCancelPreviewResponse>;

// ─── Reconciliation preview ──────────────────────────────────────────

/**
 * Строка предпросмотра инвентаризации.
 *
 * ШЕСТОЙ слой той же поломки контура (см. `StockEntryCreateResult`,
 * `StockEntrySubmitPreview`, `StockEntrySubmitResult`, `SleCancelPreviewResponse`,
 * тело отмены): схема расходилась с ручкой ВОСЕМЬЮ полями сразу — `doc_id`
 * против `reconciliation_id`, `batch_no` против `batch_id`, `physical_qty`
 * против `counted_qty`, `requires_reason` против `reason_required`, сводка
 * лежит вложенной, а `variance_value` приходит объектом «сумма + валюта», а не
 * строкой.
 *
 * Нашлось это не чтением кода: пока пустое состояние диалога говорило «Нет
 * данных», отказ разбора выглядел как «отклонений нет». Разделение двух
 * состояний, которого потребовал дизайн-гейт, вскрыло дефект первым же
 * открытием диалога — ровно то, ради чего его и требовали.
 */
export const ReconciliationPreviewLine = z.object({
  item_id: z.string().uuid(),
  /** Раунд 6 гейта C: предмет решения называется, а не кодируется хэшем. */
  item_label: z.string().nullable().optional(),
  item_sku: z.string().nullable().optional(),
  warehouse_id: z.string().uuid(),
  batch_id: z.string().uuid().nullable(),
  /** Раунд 9 гейта C: номер партии — то, чем её называет человек. */
  batch_no: z.string().nullable().optional(),
  expected_qty: z.string(),
  counted_qty: z.string(),
  variance_qty: z.string(),
  variance_value: z.object({ amount: z.string(), currency: z.string() }).nullable(),
  severity: VarianceSeverity,
  reason_required: z.boolean().default(false),
  uom: z.string().optional(),
  /** F53 (WHS-F21): строка черновика и уже введённая причина — для ввода в предпросмотре. */
  line_id: z.string().uuid().optional(),
  variance_reason: z.string().nullable().optional(),
});
export type ReconciliationPreviewLine = z.infer<typeof ReconciliationPreviewLine>;

export const ReconciliationPreviewResponse = z.object({
  reconciliation_id: z.string().uuid(),
  lines: z.array(ReconciliationPreviewLine).default([]),
  summary: z
    .object({
      total_variance_value: z.object({ amount: z.string(), currency: z.string() }).nullable(),
      items_affected: z.number().int().nonnegative(),
      /** Позиции, где отклонение требует объяснения. */
      requires_reasons: z.array(z.string()).default([]),
      /** B49 (WHS-F19): M — все позиции; K — с отклонением. */
      total: z.number().int().nonnegative().optional(),
      with_variance: z.number().int().nonnegative().optional(),
    })
    .optional(),
  /** B49 (WHS-F22): метод оценки и дата пересчёта — провенанс чисел. */
  valuation_method: z.string().optional(),
  as_of: z.string().optional(),
  /** B38 (В-23): проведение необратимо — для диалога F53. */
  irreversible: z.boolean().optional(),
  generated_at: z.string().optional(),
});
export type ReconciliationPreviewResponse = z.infer<typeof ReconciliationPreviewResponse>;

export const ReconciliationPendingDoc = z.object({
  id: z.string().uuid(),
  warehouse_id: z.string().uuid(),
  warehouse_label: z.string(),
  created_at: z.string().datetime(),
  status: z.enum(['draft', 'submitted', 'cancelled']),
  lines_count: z.number().int().nonnegative(),
  variance_value: z.string().nullable(),
});
export type ReconciliationPendingDoc = z.infer<typeof ReconciliationPendingDoc>;

// ─── Landed cost (legacy shape) ──────────────────────────────────────

export const LandedCostChargeLegacy = z.object({
  id: z.string().uuid().optional(),
  title: z.string(),
  amount: z.string(),
  currency: Currency.default('KZT'),
  basis: LandedCostBasis,
});
export type LandedCostChargeLegacy = z.infer<typeof LandedCostChargeLegacy>;

export const LandedCostSourceItemRef = z.object({
  stock_entry_id: z.string().uuid(),
  // EFE-2: добавлены для reconstruction LandedCostCreateInput payload при PUT.
  stock_entry_item_id: z.string().uuid().optional(),
  warehouse_id: z.string().uuid().optional(),
  batch_id: z.string().uuid().nullable().optional(),
  stock_entry_label: z.string().optional(),
  item_id: z.string().uuid(),
  item_label: z.string().optional(),
  qty: z.string(),
  amount: z.string(),
  weight: z.string().nullable(),
  uom: z.string(),
});
export type LandedCostSourceItemRef = z.infer<typeof LandedCostSourceItemRef>;

export const LandedCostAllocationLegacy = z.object({
  item_id: z.string().uuid(),
  item_label: z.string().optional(),
  source_entry_id: z.string().uuid(),
  qty: z.string(),
  prev_valuation_rate: z.string(),
  delta_per_unit: z.string(),
  new_valuation_rate: z.string(),
  total_allocated: z.string(),
});
export type LandedCostAllocationLegacy = z.infer<typeof LandedCostAllocationLegacy>;

export const LandedCost = z.object({
  id: z.string().uuid(),
  code: z.string(),
  status: z.enum(['draft', 'submitted', 'applied', 'cancelled']),
  posting_at: z.string().datetime(),
  total_overhead: z.string(),
  currency: Currency.default('KZT'),
  charges: z.array(LandedCostChargeLegacy).default([]),
  source_items: z.array(LandedCostSourceItemRef).default([]),
  allocations: z.array(LandedCostAllocationLegacy).default([]),
  // BE-8 follow-up: после migration 0039 scrubbed remarks — null допустим.
  notes: z.string().nullable().optional(),
  created_at: z.string().datetime(),
});
export type LandedCost = z.infer<typeof LandedCost>;

export const LandedCostDraft = z.object({
  posting_at: z.string().datetime(),
  currency: Currency.default('KZT'),
  charges: z.array(LandedCostChargeLegacy.omit({ id: true })).min(1),
  source_entry_ids: z.array(z.string().uuid()).min(1),
  notes: z.string().optional(),
});
export type LandedCostDraft = z.infer<typeof LandedCostDraft>;

// ─── 1С push (Tier 3) legacy shape ──────────────────────────────────

export const OnecPushEvent = z.object({
  id: z.string().uuid(),
  kind: OnecPushKind,
  status: OnecPushStatus,
  payload_summary: z.string(),
  document_ids: z.array(z.string().uuid()).default([]),
  period: z.string().nullable(),
  pushed_at: z.string().datetime(),
  pushed_by: z.string(),
  approver_signatures: z.array(ApprovalSignatureLegacy).default([]),
  error: z.string().nullable(),
  rolled_back_at: z.string().datetime().nullable(),
});
export type OnecPushEvent = z.infer<typeof OnecPushEvent>;

export const OnecPushHistory = z.object({
  items: z.array(OnecPushEvent),
  next_cursor: z.string().nullable().optional(),
});
export type OnecPushHistory = z.infer<typeof OnecPushHistory>;

// ─── Excel snapshots + overrides (legacy) ────────────────────────────

export const ExcelSnapshot = z.object({
  id: z.string().uuid(),
  period: z.string(),
  sha256: z.string(),
  size_bytes: z.number().int().nonnegative(),
  schema_version: z.string(),
  created_at: z.string().datetime(),
  created_by: z.string(),
  run_id: z.string().uuid().nullable().optional(),
  published: z.boolean().optional(),
  download_url: z.string().optional(),
});
export type ExcelSnapshot = z.infer<typeof ExcelSnapshot>;

export const ExcelSnapshotsResponse = z.object({
  items: z.array(ExcelSnapshot),
});
export type ExcelSnapshotsResponse = z.infer<typeof ExcelSnapshotsResponse>;

// ─── Warehouse/BOM preview (legacy) ──────────────────────────────────

export const MovePreviewResponse = z.object({
  ok: z.boolean(),
  reason: MovePreviewReason.optional(),
  affected_descendants: z.number().int().nonnegative().optional(),
  new_path: z.string().optional(),
  message: z.string().optional(),
});
export type MovePreviewResponse = z.infer<typeof MovePreviewResponse>;

export const WarehouseMoveInput = z.object({
  new_parent_id: z.string().uuid().nullable(),
});
export type WarehouseMoveInput = z.infer<typeof WarehouseMoveInput>;

export const WarehouseReorderInput = z.object({
  moves: z.array(
    z.object({
      id: z.string().uuid(),
      new_parent_id: z.string().uuid().nullable(),
      new_position: z.number().int().nonnegative().optional(),
    }),
  ),
});
export type WarehouseReorderInput = z.infer<typeof WarehouseReorderInput>;

export const BomVersion = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  is_active: z.boolean(),
  created_at: z.string().datetime(),
  created_by: z.string().optional(),
  note: z.string().optional(),
  open_work_orders_count: z.number().int().nonnegative().default(0),
});
export type BomVersion = z.infer<typeof BomVersion>;

export const BomVersionsResponse = z.object({
  items: z.array(BomVersion),
  template_key: z.string(),
});
export type BomVersionsResponse = z.infer<typeof BomVersionsResponse>;

// ─── Valuation preview / commit ──────────────────────────────────────

export const ValuationPreviewRow = z.object({
  batch_no: z.string().nullable(),
  warehouse_id: z.string().uuid().optional(),
  qty: z.string(),
  current_rate: z.string(),
  new_rate: z.string(),
  delta: z.string(),
  delta_pct: z.number(),
});
export type ValuationPreviewRow = z.infer<typeof ValuationPreviewRow>;

export const ValuationPreview = z.object({
  item_id: z.string().uuid(),
  current_method: ValuationMethod,
  new_method: ValuationMethod,
  rows: z.array(ValuationPreviewRow),
  total_delta_value: z.string(),
  affected_sle_count: z.number().int().nonnegative(),
});
export type ValuationPreview = z.infer<typeof ValuationPreview>;

export const ValuationMethodCommit = z.object({
  new_method: ValuationMethod,
  approver_id: z.string().uuid(),
  reason: z.string().min(10),
});
export type ValuationMethodCommit = z.infer<typeof ValuationMethodCommit>;

export const ValuationMethodCommitResponse = z.object({
  job_id: z.string(),
  status: JobStatus,
});
export type ValuationMethodCommitResponse = z.infer<typeof ValuationMethodCommitResponse>;

// ─── Batch QC / recall ───────────────────────────────────────────────

export const BatchQcTransitionInput = z.object({
  transition: BatchQcTransition,
  reason: z.string().min(3),
  approver_id: z.string().uuid().optional(),
});
export type BatchQcTransitionInput = z.infer<typeof BatchQcTransitionInput>;

export const BatchBulkRecallInput = z.object({
  batch_ids: z.array(z.string().uuid()).min(1),
  reason: z.string().min(10),
  approver_id: z.string().uuid(),
});
export type BatchBulkRecallInput = z.infer<typeof BatchBulkRecallInput>;

export const BatchBulkRecallResult = z.object({
  items: z.array(
    z.object({
      batch_id: z.string().uuid(),
      ok: z.boolean(),
      reason: z.string().optional(),
    }),
  ),
});
export type BatchBulkRecallResult = z.infer<typeof BatchBulkRecallResult>;

export const BatchRecallEvent = z.object({
  id: z.string().uuid(),
  batch_id: z.string().uuid(),
  at: z.string().datetime(),
  actor: z.string(),
  from_state: BatchQcState,
  to_state: BatchQcState,
  reason: z.string(),
});
export type BatchRecallEvent = z.infer<typeof BatchRecallEvent>;

// ─── Sync staging / master drift ─────────────────────────────────────

export const SyncStagingRow = z.object({
  id: z.string().uuid(),
  source: z.enum(['staiter', '1c', 'bitrix', 'adesk', 'excel', 'manual']),
  resource: z.enum(['item', 'warehouse', 'batch', 'bom', 'counterparty']),
  operation: z.enum(['upsert', 'delete']),
  detected_at: z.string().datetime(),
  resolution: z.enum(['unresolved', 'ours', 'theirs', 'merged', 'escalated']).default('unresolved'),
  subject: z.string(),
  conflict_id: z.string().uuid().nullable(),
});
export type SyncStagingRow = z.infer<typeof SyncStagingRow>;

export const SyncStagingResponse = z.object({
  items: z.array(SyncStagingRow),
  next_cursor: z.string().nullable().optional(),
});
export type SyncStagingResponse = z.infer<typeof SyncStagingResponse>;

export const MasterDriftFieldPatch = z.object({
  field: z.string(),
  pick: MasterDriftFieldPick,
  merged_value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
});
export type MasterDriftFieldPatch = z.infer<typeof MasterDriftFieldPatch>;

export const MasterDriftResolveInput = z.object({
  patches: z.array(MasterDriftFieldPatch),
  push_to_onec: z.boolean().default(true),
  note: z.string().optional(),
});
export type MasterDriftResolveInput = z.infer<typeof MasterDriftResolveInput>;

export const MasterDriftField = z.object({
  field: z.string(),
  label: z.string(),
  ours: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  theirs: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  differs: z.boolean(),
});
export type MasterDriftField = z.infer<typeof MasterDriftField>;

// ─── Bulk import ─────────────────────────────────────────────────────

export const BulkImportRowIssue = z.object({
  row: z.number().int().nonnegative(),
  column: z.string().optional(),
  severity: BulkImportIssueSeverity,
  message: z.string(),
});
export type BulkImportRowIssue = z.infer<typeof BulkImportRowIssue>;

export const BulkImportPreview = z.object({
  preview_id: z.string().uuid(),
  resource: BulkImportResource,
  total_rows: z.number().int().nonnegative(),
  will_create: z.number().int().nonnegative().default(0),
  will_update: z.number().int().nonnegative().default(0),
  will_skip: z.number().int().nonnegative().default(0),
  issues: z.array(BulkImportRowIssue).default([]),
  columns_detected: z.array(z.string()).default([]),
  import_order_hint: z.string().optional(),
});
export type BulkImportPreview = z.infer<typeof BulkImportPreview>;

export const BulkImportCommitResponse = z.object({
  job_id: z.string(),
  status: JobStatus,
});
export type BulkImportCommitResponse = z.infer<typeof BulkImportCommitResponse>;

export const BulkImportJobStatusResponse = z.object({
  job_id: z.string().uuid(),
  status: z.enum(['queued', 'active', 'completed', 'failed']),
  progress: z.number().min(0).max(100),
  result: BulkImportCommitResponse.optional(),
});
export type BulkImportJobStatusResponse = z.infer<typeof BulkImportJobStatusResponse>;

// ─── Item variant / group (legacy) ───────────────────────────────────

export const VariantAttribute = z.object({
  name: z.string(),
  options: z.array(z.string()),
});
export type VariantAttribute = z.infer<typeof VariantAttribute>;

export const ItemVariantLegacy = z.object({
  id: z.string().uuid().optional(),
  sku: z.string(),
  attributes: z.record(z.string()),
  is_active: z.boolean().default(true),
});
export type ItemVariantLegacy = z.infer<typeof ItemVariantLegacy>;

export const ItemVariantMatrix = z.object({
  item_id: z.string().uuid(),
  attributes: z.array(VariantAttribute),
  variants: z.array(ItemVariantLegacy).default([]),
});
export type ItemVariantMatrix = z.infer<typeof ItemVariantMatrix>;

export const ItemGroupLegacy = z.object({
  id: z.string().uuid(),
  name: z.string(),
  parent_id: z.string().uuid().nullable(),
});
export type ItemGroupLegacy = z.infer<typeof ItemGroupLegacy>;

export const ItemGroupsResponse = z.object({
  items: z.array(ItemGroupLegacy),
});
export type ItemGroupsResponse = z.infer<typeof ItemGroupsResponse>;

// ─── Work Order detail (legacy richer shape) ─────────────────────────

/** @deprecated W1.2 — use canonical cost breakdown (TBD) from `entities/work-order.ts`. */
export const WoCostBreakdownLegacy = z.object({
  material: z.string(),
  labor: z.string(),
  overhead: z.string(),
  total_planned: z.string(),
  total_actual: z.string().nullable(),
  variance: z.string().nullable(),
  variance_pct: z.number().nullable(),
});
export type WoCostBreakdownLegacy = z.infer<typeof WoCostBreakdownLegacy>;

/** @deprecated W1.2 — use canonical `JobCard` from `entities/work-order.ts`. */
export const JobCardLegacy = z.object({
  id: z.string().uuid(),
  work_order_id: z.string().uuid(),
  operation: z.string(),
  operator_id: z.string().uuid().nullable(),
  operator_name: z.string().nullable().optional(),
  planned_time_min: z.number().int().nonnegative(),
  actual_time_min: z.number().int().nonnegative().nullable(),
  qty_produced: z.string(),
  scrap: z.string(),
  status: z.enum(['planned', 'in_progress', 'completed']),
  started_at: z.string().datetime().nullable().optional(),
  completed_at: z.string().datetime().nullable().optional(),
});
export type JobCardLegacy = z.infer<typeof JobCardLegacy>;

/** @deprecated W1.2 — use canonical `WoWarning` from `api-contracts.ts`. */
export const WoWarningLegacy = z.object({
  type: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  message: z.string(),
  item_id: z.string().uuid().optional(),
  batch_id: z.string().uuid().optional(),
  qty_needed: z.string().optional(),
  qty_available: z.string().optional(),
  suggested_alternate_item_id: z.string().uuid().optional(),
});
export type WoWarningLegacy = z.infer<typeof WoWarningLegacy>;

/**
 * Legacy ManufacturePreviewRequired/Response — UI-форма WO release-preview.
 * Канонический ManufacturePreviewResponse (api-contracts) — иная shape (required/output).
 */
/** @deprecated W1.2 — use canonical `ManufacturePreviewRequired` from `api-contracts.ts`. */
export const ManufacturePreviewRequiredLegacy = z.object({
  item_id: z.string().uuid(),
  item_name: z.string().nullable().optional(),
  qty_required: z.string(),
  uom: z.string(),
  from_warehouse_id: z.string().uuid().nullable(),
  batch_id: z.string().uuid().nullable().optional(),
  qty_available: z.string(),
  shortage: z.string(),
  available_batches: z
    .array(
      z.object({
        batch_id: z.string().uuid(),
        batch_no: z.string(),
        qty: z.string(),
        expiry_date: z.string().date().nullable().optional(),
      }),
    )
    .default([]),
});
export type ManufacturePreviewRequiredLegacy = z.infer<typeof ManufacturePreviewRequiredLegacy>;

/** @deprecated W1.2 — use canonical `ManufacturePreviewResponse` from `api-contracts.ts`. */
export const ManufacturePreviewResponseLegacy = z.object({
  work_order_id: z.string().uuid(),
  can_release: z.boolean(),
  requirements: z.array(ManufacturePreviewRequiredLegacy).default([]),
  outputs: z
    .array(
      z.object({
        item_id: z.string().uuid(),
        item_name: z.string().nullable().optional(),
        qty: z.string(),
        warehouse_id: z.string().uuid().nullable(),
        valuation_rate: z.string().nullable().optional(),
      }),
    )
    .default([]),
  warnings: z.array(WoWarningLegacy).default([]),
  total_material_cost: z.string().nullable().optional(),
});
export type ManufacturePreviewResponseLegacy = z.infer<typeof ManufacturePreviewResponseLegacy>;

/**
 * Legacy JobCardInput (stub-shape) — используется UI-формой
 * apps/frontend/src/components/work-orders/JobCardsTable.tsx.
 * Канонический `JobCardInput` в api-contracts.ts — иная форма (operation_name, planned_qty, …).
 */
/** @deprecated W1.2 — use canonical `JobCardInput` from `api-contracts.ts`. */
export const JobCardInputLegacy = z.object({
  operation: z.string().min(1),
  operator_id: z.string().uuid().nullable().optional(),
  planned_time_min: z.number().int().nonnegative(),
  actual_time_min: z.number().int().nonnegative().nullable().optional(),
  qty_produced: z.string(),
  scrap: z.string(),
  status: z.enum(['planned', 'in_progress', 'completed']).optional(),
});
export type JobCardInputLegacy = z.infer<typeof JobCardInputLegacy>;

/**
 * @deprecated W1.4 — use canonical `WoDetail` from `entities/work-order.ts`.
 * Legacy shape остаётся для совместимости; будет удалён после полной миграции FE (U1.2).
 */
export const WoDetailLegacy = z.object({
  id: z.string().uuid(),
  code: z.string(),
  item_id: z.string().uuid(),
  item_name: z.string().nullable().optional(),
  bom_id: z.string().uuid(),
  bom_version: z.string().nullable().optional(),
  qty: z.string(),
  qty_produced: z.string(),
  state: WoState,
  planned_start_at: z.string().datetime().nullable(),
  planned_end_at: z.string().datetime().nullable(),
  actual_start_at: z.string().datetime().nullable(),
  actual_end_at: z.string().datetime().nullable(),
  executor_id: z.string().uuid().nullable(),
  executor_name: z.string().nullable().optional(),
  wip_warehouse_id: z.string().uuid().nullable(),
  fg_warehouse_id: z.string().uuid().nullable(),
  warnings: z.array(WoWarningLegacy).default([]),
  cost: WoCostBreakdownLegacy.nullable().optional(),
  job_cards: z.array(JobCardLegacy).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type WoDetailLegacy = z.infer<typeof WoDetailLegacy>;
